import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus } from "lucide-react";

export default function Signup() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    if (!first || !last) {
      toast.error("First and last name are required");
      return;
    }
    setLoading(true);
    try {
      // Backend expects a single `name` field; concatenate for storage,
      // but keep the structured fields for future-friendly UX.
      await signup({
        name: `${first} ${last}`,
        first_name: first,
        last_name: last,
        email: form.email,
        password: form.password,
      });
      toast.success("Account created!");
      navigate("/choose");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4 md:p-10 grid-bg">
      <div className="w-full max-w-lg brutal-card p-6 md:p-10">
        <Link to="/signup" data-testid="back-to-choice" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-6 hover:text-safety-yellow">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="font-heading text-3xl md:text-4xl mb-1">CREATE ACCOUNT</div>
        <div className="text-sm text-muted-foreground mb-6">Start tracking your certifications — company link is optional.</div>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">First Name</label>
              <input data-testid="signup-first-name" required className="industrial-input"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                autoComplete="given-name" />
            </div>
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">Last Name</label>
              <input data-testid="signup-last-name" required className="industrial-input"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                autoComplete="family-name" />
            </div>
          </div>
          <div>
            <label className="block font-bold uppercase text-xs tracking-wider mb-2">Email</label>
            <input data-testid="signup-email" required type="email" className="industrial-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
          </div>
          <div>
            <label className="block font-bold uppercase text-xs tracking-wider mb-2">Password (min 6)</label>
            <input data-testid="signup-password" required type="password" minLength={6} className="industrial-input"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password" />
          </div>
          <button type="submit" data-testid="signup-submit" disabled={loading} className="btn-primary w-full">
            {loading ? "Creating..." : (<><UserPlus className="w-5 h-5 mr-2" strokeWidth={3} />Create Account</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
